import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateBRD, saveManualBRD, type BRDData } from '../services/projectService';

// Configure marked to output cleaner HTML
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Helper function to remove duplicate title from AI-generated content
const removeDuplicateTitle = (text: string, projectName: string): string => {
  // Remove lines like "Business Requirements Document: Project Name" or "# BRD: Project Name"
  const lines = text.split('\n');
  const cleanedLines = lines.filter((line, index) => {
    // Only check first few lines for title
    if (index > 3) return true;

    const trimmed = line.trim();
    // Check if line is a title variant
    const isTitleLine =
      trimmed.toLowerCase().startsWith('business requirements document') ||
      trimmed.toLowerCase().startsWith('# business requirements document') ||
      trimmed.toLowerCase().startsWith('## business requirements document') ||
      trimmed.toLowerCase().startsWith('brd') ||
      trimmed.toLowerCase().startsWith('# brd') ||
      trimmed.toLowerCase().startsWith('## brd');

    return !isTitleLine;
  });

  return cleanedLines.join('\n').trim();
};

// Helper function to remove unwanted metadata and lines for clean display
const removeUnwantedLines = (text: string): string => {
  // Remove: Project Type GUID lines, Document Information section, etc.
  const lines = text.split('\n');
  const cleanedLines: string[] = [];
  let inMetadataSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip "Project Code" and "Project Type" lines
    if (line.match(/^\*{0,2}project\s*(code|type)\*{0,2}\s*:/i)) {
      continue;
    }

    // Detect start of metadata section
    if (line.toLowerCase().includes('document information') ||
        line.toLowerCase().startsWith('generated:') ||
        line.toLowerCase().startsWith('generated by:')) {
      inMetadataSection = true;
      continue;
    }

    // If we're in metadata section, skip lines that are part of it
    if (inMetadataSection) {
      // Check if this line is part of the metadata (starts with common metadata patterns)
      if (line.toLowerCase().replace(/\*/g, '').startsWith('generated') ||
          line.toLowerCase().replace(/\*/g, '').startsWith('ai model') ||
          line.toLowerCase().replace(/\*/g, '').startsWith('token usage') ||
          line.includes('This document combines') ||
          line.includes('accuracy and completeness') ||
          line.match(/^[-_*]{3,}$/) ||
          line === '') {
        continue; // Skip this line
      } else {
        // We've reached content after metadata, stop skipping
        inMetadataSection = false;
      }
    }

    // Add non-metadata lines
    if (!inMetadataSection) {
      cleanedLines.push(lines[i]); // Keep original line with indentation
    }
  }

  return cleanedLines.join('\n').trim();
};

// Helper function to safely parse and sanitize markdown
const parseMarkdown = (text: string): string => {
  try {
    const result = marked.parse(text);
    return DOMPurify.sanitize(typeof result === 'string' ? result : '');
  } catch (error) {
    console.error('Markdown parsing error:', error);
    return DOMPurify.sanitize(text);
  }
};

/**
 * Modal States:
 * - choice: Initial choice between AI Generate or Manual Write
 * - ai-generating: AI is processing the generation
 * - ai-result: AI generation complete, showing result with regenerate/edit options
 * - manual-write: Manual text editor for writing BRD
 * - view: Display existing BRD with metadata
 */
type ModalState = 'choice' | 'ai-generating' | 'ai-result' | 'manual-write' | 'view';

interface BRDModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  companyName?: string; // Company name for header display
  token: string;
  existingBRD?: string | null;
  existingMethod?: 'ai' | 'manual' | null;
  existingGeneratedAt?: string | null;
  existingTokensUsed?: number | null;
  existingModel?: string | null;
  existingGeneratedBy?: string | null; // User name who created the BRD
  currentUserName?: string; // Current logged-in user's name
  onBRDUpdated: () => void; // Callback to refresh project data
  readOnly?: boolean; // When true, hide edit/regenerate controls
  startDate?: string | null;
  targetCompletionDate?: string | null;
  requirements?: Array<{ id: string; due_date?: string | null; is_active?: boolean; requirement_code?: string; title: string }>;
  milestones?: Array<{ id: string; milestone_date: string; description: string }>;
}

export const BRDModal: React.FC<BRDModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  companyName,
  token,
  existingBRD,
  existingMethod,
  existingGeneratedAt,
  existingTokensUsed,
  existingModel,
  existingGeneratedBy,
  currentUserName,
  onBRDUpdated,
  readOnly,
  startDate,
  targetCompletionDate,
  requirements: reqsProp,
  milestones: milestonesProp
}) => {
  // Determine initial state based on existing BRD
  const getInitialState = (): ModalState => {
    if (existingBRD) return 'view';
    return 'choice';
  };

  const [modalState, setModalState] = useState<ModalState>(getInitialState());
  const [manualText, setManualText] = useState<string>(existingBRD || '');
  const [aiGeneratedText, setAiGeneratedText] = useState<string>('');
  const [aiMetadata, setAiMetadata] = useState<{ tokens?: number; model?: string }>({});
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // Ref for the document content to export
  const documentRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens/closes or existing BRD changes
  useEffect(() => {
    if (isOpen) {
      setModalState(getInitialState());
      setManualText(existingBRD || '');
      setAiGeneratedText('');
      setAiMetadata({});
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen, existingBRD]);

  // Timeline helpers
  const computeEstimatedDays = (start: string, end: string): number | null => {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return null;
    return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatTimelineDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const parseLocalDate = (dateString: string): Date => {
    const parts = dateString.split('T')[0].split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const renderTimeline = () => {
    if (!startDate || !targetCompletionDate) return null;
    const days = computeEstimatedDays(startDate, targetCompletionDate);
    if (days === null) return null;

    const s = parseLocalDate(startDate);
    const e = parseLocalDate(targetCompletionDate);
    const range = e.getTime() - s.getTime();

    return (
      <div className="px-6 py-5 bg-slate-50 border-b border-slate-200">
        <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-3">Project Timeline</p>
        <div className="flex items-center gap-5">
          {/* Timeline bar section */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-slate-700">{formatTimelineDate(startDate)}</span>
              <span className="text-xs font-semibold text-slate-700">{formatTimelineDate(targetCompletionDate)}</span>
            </div>
            <div className="relative h-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 shadow-sm">
              {/* Start dot */}
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-indigo-500 shadow-sm" />
              {/* End dot */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-500 shadow-sm" />
              {/* Requirement due-date markers */}
              {reqsProp && reqsProp
                .filter((req) => req.due_date && req.is_active)
                .map((req) => {
                  const dueMs = parseLocalDate(req.due_date!).getTime();
                  const pct = Math.max(0, Math.min(100, ((dueMs - s.getTime()) / range) * 100));
                  return (
                    <div
                      key={req.id}
                      className="group absolute top-1/2 -translate-y-1/2 -translate-x-[5px] z-10"
                      style={{ left: `${pct}%` }}
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-white border-2 border-gray-800 cursor-pointer shadow-sm" />
                      <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                        <div className="bg-white shadow-lg rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap border border-gray-200">
                          <div>
                            <span className="font-semibold text-gray-900">{req.requirement_code || 'REQ'}</span>
                            <span className="text-gray-600"> — {req.title.length > 40 ? req.title.slice(0, 40) + '…' : req.title}</span>
                          </div>
                          <div className="mt-1 text-[11px] text-gray-500">
                            Due: {parseLocalDate(req.due_date!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {/* Milestone markers (diamonds) */}
              {milestonesProp && milestonesProp
                .filter((m) => m.milestone_date)
                .map((m) => {
                  const mDate = parseLocalDate(m.milestone_date.toString().slice(0, 10));
                  const pct = Math.max(0, Math.min(100, ((mDate.getTime() - s.getTime()) / range) * 100));
                  return (
                    <div
                      key={`ms-${m.id}`}
                      className="group absolute top-1/2 -translate-y-1/2 -translate-x-[7px] z-20"
                      style={{ left: `${pct}%` }}
                    >
                      <div className="w-3.5 h-3.5 rotate-45 bg-indigo-500 border-2 border-white cursor-pointer shadow-sm" />
                      <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                        <div className="bg-white shadow-lg rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap border border-gray-200">
                          <div className="font-semibold text-indigo-600">Milestone</div>
                          <div className="text-gray-900">{m.description}</div>
                          <div className="mt-1 text-[11px] text-gray-500">
                            {mDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          {/* Hero badge */}
          <div className="shrink-0 flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-indigo-50 border border-indigo-200">
            <span className="text-2xl font-bold text-indigo-700 leading-none">{days}</span>
            <span className="text-[10px] font-semibold text-indigo-500 mt-1 tracking-wide">Est. Days</span>
          </div>
        </div>
      </div>
    );
  };

  const handleClose = () => {
    setError('');
    setIsSubmitting(false);
    onClose();
  };

  const handleAIGenerate = async () => {
    setModalState('ai-generating');
    setError('');

    try {
      const result: BRDData = await generateBRD(projectId, token);
      setAiGeneratedText(result.brd_content);
      setAiMetadata({
        tokens: result.tokens_used,
        model: result.model
      });
      setModalState('ai-result');
      onBRDUpdated(); // Refresh project data
    } catch (err: any) {
      setError(err.message || 'Failed to generate BRD. Please try again.');
      setModalState('choice');
    }
  };

  const handleManualSave = async () => {
    if (!manualText.trim()) {
      setError('Please enter a BRD before saving.');
      return;
    }

    if (manualText.trim().length < 500) {
      setError('BRD must be at least 500 characters.');
      return;
    }

    if (manualText.trim().length > 50000) {
      setError('BRD cannot exceed 50,000 characters.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await saveManualBRD(projectId, manualText, token);
      onBRDUpdated(); // Refresh project data
      setModalState('view');
    } catch (err: any) {
      setError(err.message || 'Failed to save BRD. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditExisting = () => {
    setManualText(existingBRD || '');
    setModalState('manual-write');
  };

  const handleRegenerateAI = () => {
    setModalState('choice');
  };

  const handleExportPDF = async () => {
    setIsExportingPDF(true);

    try {
      // Get the BRD text (either AI generated or existing)
      const rawBRDText = modalState === 'ai-result' ? aiGeneratedText : (existingBRD || '');
      // Remove duplicate title and unwanted lines from the content (for PDF export only)
      let brdText = removeDuplicateTitle(rawBRDText, projectName);
      brdText = removeUnwantedLines(brdText);

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      let yPosition = margin;

      // Add header background
      pdf.setFillColor(71, 85, 105); // slate-700
      pdf.rect(0, 0, pageWidth, 40, 'F');

      // Add header text - left side
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Business Requirements Document', margin, 15);

      // Add company name if available
      if (companyName) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(companyName, margin, 23);
      }

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(projectName, margin, companyName ? 31 : 30);

      // Add creator info - right side
      const creatorName = modalState === 'ai-result'
        ? (currentUserName || 'AI Assistant')
        : (existingGeneratedBy || 'Unknown');

      pdf.setFontSize(8);
      pdf.text('Created by', pageWidth - margin, 20, { align: 'right' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text(creatorName, pageWidth - margin, 28, { align: 'right' });
      pdf.setFont('helvetica', 'normal');

      yPosition = 50;

      // Project Timeline (if dates available)
      if (startDate && targetCompletionDate) {
        const pdfDays = computeEstimatedDays(startDate, targetCompletionDate);
        if (pdfDays !== null) {
          const tlY = yPosition;

          // "PROJECT TIMELINE" label
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(148, 163, 184); // slate-400
          pdf.text('PROJECT TIMELINE', margin, tlY);

          const barY = tlY + 8;
          const barHeight = 4;
          const badgeW = 22;
          const badgeH = 22;
          const barRight = pageWidth - margin - badgeW - 6;
          const barLeft = margin;
          const barWidth = barRight - barLeft;

          // Date labels
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(51, 65, 85); // slate-700
          pdf.text(formatTimelineDate(startDate), barLeft, barY - 1);
          pdf.text(formatTimelineDate(targetCompletionDate), barRight, barY - 1, { align: 'right' });

          // Gradient bar (indigo-500 to blue-500 approximation: left indigo, right blue)
          // Draw as two halves for a gradient effect
          pdf.setFillColor(99, 102, 241); // indigo-500
          pdf.roundedRect(barLeft, barY, barWidth / 2, barHeight, 2, 2, 'F');
          pdf.setFillColor(59, 130, 246); // blue-500
          pdf.roundedRect(barLeft + barWidth / 2, barY, barWidth / 2, barHeight, 2, 2, 'F');
          // Fill center seam
          pdf.setFillColor(79, 116, 244);
          pdf.rect(barLeft + barWidth / 2 - 1, barY, 2, barHeight, 'F');

          // Endpoint dots
          const dotR = 2.2;
          const dotCY = barY + barHeight / 2;
          // Start dot
          pdf.setFillColor(255, 255, 255);
          pdf.circle(barLeft, dotCY, dotR, 'F');
          pdf.setDrawColor(99, 102, 241); // indigo-500
          pdf.setLineWidth(0.6);
          pdf.circle(barLeft, dotCY, dotR, 'S');
          // End dot
          pdf.setFillColor(255, 255, 255);
          pdf.circle(barRight, dotCY, dotR, 'F');
          pdf.setDrawColor(59, 130, 246); // blue-500
          pdf.circle(barRight, dotCY, dotR, 'S');

          // Hero badge
          const badgeX = pageWidth - margin - badgeW;
          const badgeY = tlY - 2;
          pdf.setFillColor(238, 242, 255); // indigo-50
          pdf.roundedRect(badgeX, badgeY, badgeW, badgeH, 3, 3, 'F');
          pdf.setDrawColor(199, 210, 254); // indigo-200
          pdf.setLineWidth(0.4);
          pdf.roundedRect(badgeX, badgeY, badgeW, badgeH, 3, 3, 'S');
          // Day count
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(67, 56, 202); // indigo-700
          pdf.text(String(pdfDays), badgeX + badgeW / 2, badgeY + 10, { align: 'center' });
          // "EST. DAYS" sublabel
          pdf.setFontSize(5);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(99, 102, 241); // indigo-500
          pdf.text('EST. DAYS', badgeX + badgeW / 2, badgeY + 15, { align: 'center' });

          // Separator line
          yPosition = barY + barHeight + 8;
          pdf.setDrawColor(203, 213, 225); // slate-300
          pdf.setLineWidth(0.3);
          pdf.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 6;
        }
      }

      // Reset text color for content
      pdf.setTextColor(51, 65, 85); // slate-700

      // Parse and render content with table support
      const lines = brdText.split('\n');
      let i = 0;

      while (i < lines.length) {
        const line = lines[i].trim();

        // Skip empty lines but add spacing
        if (!line) {
          yPosition += 3;
          i++;
          continue;
        }

        // Check for horizontal rule (---, ___, ***)
        if (line.match(/^[-_*]{3,}$/)) {
          yPosition += 4;
          pdf.setDrawColor(203, 213, 225); // slate-300
          pdf.setLineWidth(0.5);
          pdf.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8;
          i++;
          continue;
        }

        // Check for markdown table (starts with |)
        if (line.startsWith('|')) {
          // Collect all table lines
          const tableLines: string[] = [];
          while (i < lines.length && lines[i].trim().startsWith('|')) {
            tableLines.push(lines[i].trim());
            i++;
          }

          // Parse table
          if (tableLines.length >= 2) { // Need at least header + separator
            // Extract header row (first line)
            const headerRow = tableLines[0]
              .split('|')
              .map(cell => cell.trim())
              .filter(cell => cell); // Remove empty cells from start/end

            // Skip separator row (second line, contains dashes)
            // Extract data rows (remaining lines)
            const dataRows = tableLines.slice(2).map(row =>
              row.split('|')
                .map(cell => cell.trim())
                .filter(cell => cell)
            );

            // Check if we need a new page before table
            if (yPosition > pageHeight - 60) {
              pdf.addPage();
              yPosition = margin;
            }

            // Render table using autoTable — clean style matching HTML preview
            autoTable(pdf, {
              startY: yPosition,
              head: [headerRow],
              body: dataRows,
              theme: 'plain',
              styles: {
                fontSize: 9,
                cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
                textColor: [30, 41, 59], // slate-800
                font: 'helvetica',
                lineColor: [203, 213, 225], // slate-300
                lineWidth: 0,
              },
              headStyles: {
                fillColor: false as any,
                textColor: [30, 41, 59], // slate-800
                fontStyle: 'bold',
                halign: 'left',
              },
              columnStyles: {
                0: { cellWidth: 30 }, // ID/Requirement Code column
                1: { cellWidth: 45 }, // Title column
                2: { cellWidth: 25 }, // Priority column
                3: { cellWidth: 'auto' }, // Description takes remaining space
              },
              margin: { left: margin, right: margin },
              tableWidth: contentWidth,
              didParseCell: (data: any) => {
                // Horizontal lines only — bottom border on every cell
                data.cell.styles.lineWidth = { top: 0, right: 0, bottom: 0.3, left: 0 };
                data.cell.styles.lineColor = [203, 213, 225]; // slate-300
              },
            });

            // Get final Y position after table
            yPosition = (pdf as any).lastAutoTable.finalY + 8;

            continue; // Skip the normal line increment
          }
        }

        // Check for headers (lines starting with #)
        const headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
        const isHeader = headerMatch !== null;

        // Check for bullet points
        const isBullet = line.startsWith('- ') || line.startsWith('* ') || line.match(/^\d+\. /);

        if (isHeader) {
          // Add extra spacing before headers (except first one)
          if (yPosition > 60) {
            yPosition += 6;
          }

          // Get header level and text
          const headerLevel = headerMatch![1].length;
          const headerText = headerMatch![2].replace(/\*\*/g, '').trim(); // Also remove ** if present

          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFont('helvetica', 'bold');
          // Size based on header level: # = 14pt, ## = 12pt, ### = 11pt
          const fontSize = headerLevel === 1 ? 14 : headerLevel === 2 ? 12 : 11;
          pdf.setFontSize(fontSize);

          // Split header if too long
          const headerLines = pdf.splitTextToSize(headerText, contentWidth);
          pdf.text(headerLines, margin, yPosition);
          yPosition += headerLines.length * (fontSize === 14 ? 8 : 7);

          // Reset font for body text
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
        } else if (isBullet) {
          // Bullet point
          // Remove bullet marker and clean text (also remove ** markdown)
          let bulletText = line
            .replace(/^[-*]\s+/, '')  // Remove - or * bullet
            .replace(/^\d+\.\s+/, '') // Remove numbered list
            .replace(/\*\*/g, '')     // Remove all ** bold markers
            .trim();

          // Check if we need a new page
          if (yPosition > pageHeight - margin - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          // Render bullet
          const bulletIndent = margin + 3;
          const textIndent = bulletIndent + 7;
          const maxWidth = contentWidth - 10;

          // Add bullet point
          pdf.setFontSize(10);
          pdf.text('•', bulletIndent, yPosition);

          // Add text
          pdf.setFont('helvetica', 'normal');
          const textLines = pdf.splitTextToSize(bulletText, maxWidth);
          pdf.text(textLines, textIndent, yPosition);
          yPosition += textLines.length * 5.5;

        } else {
          // Regular paragraph text - remove all markdown
          let cleanText = line.replace(/\*\*/g, '');

          // Check if we need a new page for this paragraph
          if (yPosition > pageHeight - margin - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          // Render paragraph
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          const textLines = pdf.splitTextToSize(cleanText, contentWidth);
          pdf.text(textLines, margin, yPosition);
          yPosition += textLines.length * 5.5;
        }

        i++; // Move to next line
      }

      // Add footer on all pages
      const totalPages = pdf.internal.pages.length - 1; // Subtract 1 for the internal blank page
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184); // slate-400
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }

      // Generate filename
      const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_BRD_${new Date().toISOString().split('T')[0]}.pdf`;

      // Download
      pdf.save(fileName);
    } catch (error) {
      console.error('PDF export error:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              {companyName && <p className="text-sm text-gray-500">{companyName}</p>}
              <p className="text-sm text-gray-600">{projectName}</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* AI Generation Indicator (not exported) */}
              {(modalState === 'ai-result' || (modalState === 'view' && existingMethod === 'ai')) && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  AI Generated
                </span>
              )}
              {(modalState === 'view' && existingMethod === 'manual') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Manually Written
                </span>
              )}

              {/* Export PDF Button (visible when viewing/showing BRD) */}
              {(modalState === 'ai-result' || modalState === 'view') && (
                <button
                  onClick={handleExportPDF}
                  disabled={isExportingPDF}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Export to PDF"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isExportingPDF ? 'Exporting...' : 'Export PDF'}
                </button>
              )}

              {/* Edit Button (visible when viewing existing BRD, hidden in readOnly) */}
              {modalState === 'view' && !readOnly && (
                <button
                  onClick={handleEditExisting}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  title="Edit BRD"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}

              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* State: Choice */}
          {modalState === 'choice' && !readOnly && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-6">
                How would you like to create the Business Requirements Document for this project?
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* AI Generate Option */}
                <button
                  onClick={handleAIGenerate}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl font-bold group-hover:scale-110 transition-transform">
                      AI
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">AI Generate</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Automatically generate a comprehensive 10-15 page Business Requirements Document based on your project data using AI.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    Requires at least 1 requirement to generate.
                  </p>
                </button>

                {/* Manual Write Option */}
                <button
                  onClick={() => setModalState('manual-write')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
                >
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <h3 className="ml-3 text-lg font-semibold text-gray-900">Write Manually</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Write your own custom Business Requirements Document with full control over the content and structure.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    500-50,000 characters required.
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Read-only empty state */}
          {modalState === 'choice' && readOnly && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 font-medium">No BRD has been created yet</p>
              <p className="text-sm text-gray-400 mt-1">A project manager or admin can generate one.</p>
            </div>
          )}

          {/* State: AI Generating */}
          {modalState === 'ai-generating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative w-24 h-24 mb-6">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 border-4 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-lg font-bold">
                    AI
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Business Requirements Document...</h3>
              <p className="text-sm text-gray-600 text-center max-w-md">
                Our AI is analyzing your project data and creating a comprehensive BRD. This typically takes a minute or two.
              </p>
            </div>
          )}

          {/* State: AI Result */}
          {modalState === 'ai-result' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-900">AI Generation Complete</span>
                </div>
                {aiMetadata.tokens && (
                  <p className="text-xs text-gray-600">
                    Generated using {aiMetadata.model || 'OpenAI'} ({aiMetadata.tokens.toLocaleString()} tokens)
                  </p>
                )}
              </div>

              {/* Enhanced BRD Display */}
              <div ref={documentRef} className="bg-gradient-to-br from-slate-50 via-white to-blue-50 border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {/* Document Header */}
                <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 border-b border-slate-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Project Plan / Business Requirements Document</h3>
                      {companyName && <p className="text-xs text-slate-200 mt-0.5">{companyName}</p>}
                      <p className="text-xs text-slate-300 mt-0.5">{projectName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-300">Created by</p>
                      <p className="text-sm font-medium text-white">{currentUserName || 'AI Assistant'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Timeline */}
                {renderTimeline()}

                {/* Document Body */}
                <div className="px-8 py-6 bg-white">
                  <div
                    className="prose prose-slate max-w-none
                      prose-headings:font-bold prose-headings:text-slate-900
                      prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                      prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
                      prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
                      prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-slate-900 prose-strong:font-semibold
                      prose-ul:my-3 prose-li:text-slate-700"
                    style={{
                      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                      fontSize: '15px',
                      lineHeight: '1.7'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(removeUnwantedLines(removeDuplicateTitle(aiGeneratedText, projectName))) }}
                  />
                </div>

                {/* Document Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Generated {new Date().toLocaleString()}
                    </span>
                    <span className="text-slate-500">Powered by {aiMetadata.model || 'OpenAI'}</span>
                  </div>
                </div>
              </div>

              {/* Token count and actions (not exported) */}
              <div className="flex items-center justify-between pt-4">
                {aiMetadata.tokens && (
                  <span className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {aiMetadata.tokens.toLocaleString()} tokens used
                  </span>
                )}
                {!readOnly && (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleRegenerateAI}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      Regenerate
                    </button>
                    <button
                      onClick={() => {
                        setManualText(aiGeneratedText);
                        setModalState('manual-write');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                    >
                      Edit & Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* State: Manual Write */}
          {modalState === 'manual-write' && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  Write a comprehensive Business Requirements Document (10-15 pages recommended). Include sections like Executive Summary, Business Objectives, Project Scope, Stakeholders, Requirements, and Implementation Approach.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Character count: {manualText.length} / 50,000 (minimum: 500)
                </p>
              </div>

              <textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-sans"
                placeholder="Enter your Business Requirements Document here..."
              />

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setModalState(existingBRD ? 'view' : 'choice')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualSave}
                  disabled={isSubmitting || manualText.trim().length < 500}
                  className="px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save BRD'}
                </button>
              </div>
            </div>
          )}

          {/* State: View */}
          {modalState === 'view' && existingBRD && (
            <div className="space-y-4">
              {/* Enhanced BRD Display */}
              <div ref={documentRef} className="bg-gradient-to-br from-slate-50 via-white to-blue-50 border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {/* Document Header */}
                <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-4 border-b border-slate-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">Project Plan / Business Requirements Document</h3>
                      {companyName && <p className="text-xs text-slate-200 mt-0.5">{companyName}</p>}
                      <p className="text-xs text-slate-300 mt-0.5">{projectName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-300">Created by</p>
                      <p className="text-sm font-medium text-white">{existingGeneratedBy || 'Unknown'}</p>
                    </div>
                  </div>
                </div>

                {/* Project Timeline */}
                {renderTimeline()}

                {/* Document Body */}
                <div className="px-8 py-6 bg-white">
                  <div
                    className="prose prose-slate max-w-none
                      prose-headings:font-bold prose-headings:text-slate-900
                      prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                      prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
                      prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
                      prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-slate-900 prose-strong:font-semibold
                      prose-ul:my-3 prose-li:text-slate-700"
                    style={{
                      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                      fontSize: '15px',
                      lineHeight: '1.7'
                    }}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(removeUnwantedLines(removeDuplicateTitle(existingBRD, projectName))) }}
                  />
                </div>

                {/* Document Footer */}
                <div className="bg-slate-50 px-6 py-3 border-t border-slate-200">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      {existingGeneratedAt ? new Date(existingGeneratedAt).toLocaleString() : 'Date not available'}
                    </span>
                    {existingMethod === 'ai' && existingModel && (
                      <span className="text-slate-500">Powered by {existingModel}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Token count and actions (not exported) */}
              {existingMethod === 'ai' && (
                <div className="flex items-center justify-between pt-4">
                  {existingTokensUsed && (
                    <span className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      {existingTokensUsed.toLocaleString()} tokens used
                    </span>
                  )}
                  {!readOnly && (
                    <button
                      onClick={handleRegenerateAI}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      Regenerate with AI
                    </button>
                  )}
                </div>
              )}

              {/* Recreate with AI button for manual BRDs */}
              {existingMethod === 'manual' && !readOnly && (
                <div className="flex items-center justify-end pt-4">
                  <button
                    onClick={handleRegenerateAI}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Recreate with AI
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer (only show close button in view mode) */}
        {modalState === 'view' && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
